import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import style from "./mission.module.css";

import startProjectImg from "../img/start-project.png";

/** 간단한 페이드인 유틸 */
function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setOn(true)),
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{
        opacity: on ? 1 : 0,
        transform: on ? "translateY(0)" : "translateY(30px)",
        transition: `opacity .8s ease ${delay}s, transform .8s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

const VISION = [
  { title: "Innovation", desc: "최첨단 AI 기술을 활용하여 제조업의 패러다임을 변화시키며, 지속적인 혁신을 통해 미래를 선도합니다." },
  { title: "Precision", desc: "엔지니어링의 정밀함과 데이터 기반 분석을 통해 완벽한 솔루션을 제공하며, 품질에 대한 타협은 없습니다." },
  { title: "Partnership", desc: "고객과의 긴밀한 파트너십을 바탕으로 함께 성장하며, 상호 발전하는 지속 가능한 관계를 구축합니다." },
];

const DOMAINS = [
  { title: "Advanced Manufacturing", desc: "AI 기반 스마트 팩토리 구축 및 자동화 생산 시스템 최적화" },
  { title: "Robotics", desc: "산업용 로봇 및 지능형 자동화 시스템 설계 및 구현" },
  { title: "Computer Vision", desc: "AI 비전 시스템을 통한 품질 검사 및 실시간 모니터링 솔루션" },
];

const STEPS = [
  { num: "01", title: "Analysis", desc: "고객의 니즈를 정확히 파악하고 맞춤형 솔루션 설계를 위한 상세 분석을 진행합니다." },
  { num: "02", title: "Development", desc: "최첨단 기술을 활용하여 효율적이고 혁신적인 시스템을 설계하고 개발합니다." },
  { num: "03", title: "Validation", desc: "엄격한 품질 기준에 따라 철저한 테스트를 실시하여 안전성과 신뢰성을 보장합니다." },
  { num: "04", title: "Implementation", desc: "성공적인 시스템 구축과 지속적인 기술 지원을 통해 고객 만족을 실현합니다." },
];

export default function JoinMission() {
  /** 👇 페이지 내 전용 이미지 팝업 상태 */
  const [openPoster, setOpenPoster] = useState(false);

  /** 팝업 열릴 때 바디 스크롤 잠금 & ESC 닫기 */
  useEffect(() => {
    if (openPoster) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenPoster(false);
      window.addEventListener("keydown", onKey);
      return () => {
        document.body.style.overflow = prev;
        window.removeEventListener("keydown", onKey);
      };
    }
  }, [openPoster]);

  return (
    <>
      <Header />
        <div className={style["main-content"]}>
            {/* HERO */}
            <section className={style["container--front"]}>
                <div className={style["unit--front--head"]}>
                <h1 className={style["text--front--head"]}>Join The Mission</h1>
                <p className={style["text--front--copy"]} style={{ paddingTop: 24, fontSize: 18 }}>
                    From Idea to Reality
                    <br />
                    AI-Driven Manufacturing으로 혁신의 새로운 기준을 만들어갑니다
                </p>
                </div>
            </section>

            {/* Our Vision */}
            <section className={style["container--portfolio"]}>
                <div className={style["unit--content"]}>
                <FadeIn>
                    <h2 className={style["text--content"]}>Our Vision</h2>
                    <p className={style["text--content--copy"]} style={{ width: "min(700px,100%)" }}>
                    기술과 혁신으로 제조업의 미래를 설계합니다
                    </p>

                    <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                        gap: 20,
                        marginTop: 36,
                    }}
                    >
                    {VISION.map((v) => (
                        <article
                        key={v.title}
                        style={{
                            background: "linear-gradient(145deg,#2a2a2a,#1e1e1e)",
                            borderLeft: "4px solid #3498db",
                            borderRadius: 12,
                            padding: "32px 26px",
                            border: "1px solid #333",
                            color: "#ffffff",
                        }}
                        >
                        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>{v.title}</div>
                        <p style={{ color: "#cccccc", lineHeight: 1.8 }}>{v.desc}</p>
                        </article>
                    ))}
                    </div>
                </FadeIn>
                </div>
            </section>

            {/* Core Domains */}
            <section className={style["container--portfolio"]}>
                <div className={style["unit--content"]}>
                <FadeIn delay={0.2}>
                    <h2 className={style["text--content"]}>Core Domains</h2>
                    <p className={style["text--content--copy"]} style={{ width: "min(700px,100%)" }}>
                    첨단 기술로 다양한 산업 분야의 혁신을 이끌어갑니다
                    </p>

                    <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: 20,
                        marginTop: 28,
                    }}
                    >
                    {DOMAINS.map((d) => (
                        <article
                        key={d.title}
                        style={{
                            background: "#2a2a2a",
                            border: "1px solid #333",
                            borderRadius: 10,
                            padding: "22px 18px",
                        }}
                        >
                        <div style={{ color: "#3498db", fontWeight: 600, marginBottom: 8, letterSpacing: 0.3 }}>
                            {d.title}
                        </div>
                        <div style={{ color: "#cccccc", fontSize: 14, lineHeight: 1.7 }}>{d.desc}</div>
                        </article>
                    ))}
                    </div>
                </FadeIn>
                </div>
            </section>

            {/* Process */}
            <section className={style["container--portfolio"]}>
                <div className={style["unit--content"]}>
                <FadeIn delay={0.4}>
                    <h2 className={style["text--content"]}>Process</h2>
                    <p className={style["text--content--copy"]} style={{ width: "min(700px,100%)" }}>
                    체계적이고 전문적인 접근으로 최상의 결과를 보장합니다
                    </p>

                    <div
                    style={{
                        background: "linear-gradient(135deg,#2c3e50,#34495e)",
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,.12)",
                        padding: "30px 22px",
                        marginTop: 24,
                    }}
                    >
                    <div
                        style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, auto)",
                        gridTemplateRows: "repeat(2, auto)",
                        gap: 20,
                        }}
                    >
                        {STEPS.map((s) => (
                        <div key={s.num} style={{ position: "relative", paddingLeft: 56 }}>
                            <div
                            style={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                width: 40,
                                height: 40,
                                borderRadius: 6,
                                background: "linear-gradient(135deg,#3498db,#2ecc71)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 14,
                            }}
                            >
                            {s.num}
                            </div>
                            <div style={{ fontWeight: 700, marginBottom: 8, color: "#ffffff" }}>{s.title}</div>
                            <div style={{ color: "#e6e6e6", fontSize: 14, lineHeight: 1.7 }}>{s.desc}</div>
                        </div>
                        ))}
                    </div>
                    </div>
                </FadeIn>
                </div>
            </section>

            {/* CTA */}
            <section className={style["container--portfolio"]}>
                <div className={style["unit--content"]}>
                <FadeIn delay={0.6}>
                    <div
                    style={{
                        textAlign: "center",
                        background: "linear-gradient(135deg,#1e1e1e,#2a2a2a)",
                        borderRadius: 12,
                        border: "1px solid #333",
                        padding: "48px 24px",
                    }}
                    >
                    <h3 className={style["text--content"]} style={{ marginBottom: 14, fontWeight: 300 }}>
                        Ready to Transform
                    </h3>
                    <p className={style["text--content--copy"]} style={{ width: "min(720px,100%)" }}>
                        혁신적인 기술과 엔지니어링 정신으로 무장한 THE ENGINEER와 함께 새로운 가능성을 발견하고
                        아이디어를 현실로 만들어보세요.
                    </p>
                    <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 18 }}>
                        {/* 👇 페이지 내 팝업으로 긴 이미지 열기 */}
                        <a
                        className={style["button--about"]}
                        href="#start"
                        onClick={(e) => {
                            e.preventDefault();
                            setOpenPoster(true);
                        }}
                        >
                        Start Project
                        </a>
                        <a
                            className={style["button--about"]}
                            href="#contact"
                            onClick={(e) => {
                                e.preventDefault();
                                window.dispatchEvent(new CustomEvent("inbound:open"));
                            }}
                        >
                        Contact Us
                        </a>
                    </div>
                    </div>
                </FadeIn>
                </div>
            </section>
        </div>

      <Footer />

      {/* === 페이지 전용 이미지 팝업 === */}
      {openPoster && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            // 내용 바깥(배경) 클릭 시 닫기
            if (e.target === e.currentTarget) setOpenPoster(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            className="container-sheet"
            style={{
              position: "relative",
              background: "#0f0f0f",
              border: "1px solid #2a2a2a",
              borderRadius: 12,
              maxHeight: "90vh",
              maxWidth: "min(900px, 92vw)",
              overflow: "auto",
              padding: 12,
              boxShadow: "0 10px 40px rgba(0,0,0,.6)",
            }}
          >
            {/* 닫기 버튼 */}
            <button
                onClick={() => setOpenPoster(false)}
                aria-label="닫기"
                style={{
                    position: "sticky",
                    top: 0,
                    float: "right",
                    margin: 4,
                    border: 0,
                    background: "transparent",
                    cursor: "pointer",
                }}
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <line x1="4" y1="4" x2="16" y2="16" stroke="#D6D6D6" strokeWidth="2" />
                    <line x1="16" y1="4" x2="4" y2="16" stroke="#D6D6D6" strokeWidth="2" />
                </svg>
            </button>

            {/* 길~다란 이미지 (가로는 자동 맞춤, 세로는 스크롤) */}
            <img
              src={startProjectImg}
              alt="Start Project 안내"
              style={{ display: "block", width: "100%", height: "auto" }}
            />
          </div>
        </div>
      )}
    </>
  );
}
